import {YKeyValue} from 'y-utility/y-keyvalue'
import * as Y from 'yjs'
import {DATABASE_COLUMN_NAME_MAX_CHARS} from '../../../utils/constants'

/**
 * yjs data layout:
 * - rows: Array<RowId>
 * - columns: Array<ColumnId>
 * - columnMeta: Map<ColumnId, ColumnMeta>
 * - data: Map<RowId, Array<{key: ColumnId, val: string | null}>>
 *
 * Future:
 * - views: Array<ViewId>
 * - viewMeta: Map<ViewId, View>
 */

 






















const defaultColumnMeta = {
  name: 'New column',
  type: 'text'
}

export const generateId = (_doc) => {
  return crypto.randomUUID()
}

export const getRows = (doc) => {
  return doc.getArray('rows')
}

export const getColumns = (doc) => {
  return doc.getArray('columns')
}

export const getColumnMeta = (doc) => {
  return doc.getMap('columnMeta')
}

export const getData = (doc) => {
  return doc.getMap('data')
}

export const getDataEntries = function* (doc) {
  const data = getData(doc)
  for (const [rowId, value] of data) {
    yield [rowId, new YKeyValue(value)] 
  }
}

export const getRowData = (doc, rowId) => {
  const data = getData(doc)
  const row = data.get(rowId)
  if (!row) {
    return null
  }
  return new YKeyValue(row)
}

export const changeColumn = (doc, columnId, newMeta) => {
  const columnMeta = doc.getMap('columnMeta')
  columnMeta.set(columnId, {
    ...newMeta,
    name: newMeta.name.substring(0, DATABASE_COLUMN_NAME_MAX_CHARS)
  })
}

const insertColumnAt = (doc, index, meta = defaultColumnMeta) => {
  const id = generateId(doc)
  doc.transact(() => {
    changeColumn(doc, id, meta)
    const columns = doc.getArray('columns')
    columns.insert(index, [id])
  })
  return id
}

export const insertColumnBefore = (
  doc,
  columnId,
  meta = defaultColumnMeta
) => {
  const columns = doc.getArray('columns')
  const index = columns.toArray().indexOf(columnId)
  return insertColumnAt(doc, index, meta)
}

export const insertColumnAfter = (
  doc,
  columnId,
  meta = defaultColumnMeta
) => {
  const columns = doc.getArray('columns')
  const index = columns.toArray().indexOf(columnId)
  return insertColumnAt(doc, index + 1, meta)
}

export const appendColumn = (doc, meta = defaultColumnMeta) => {
  const id = generateId(doc)
  doc.transact(() => {
    changeColumn(doc, id, meta)
    const columns = doc.getArray('columns')
    columns.push([id])
  })
  return id
}

export const duplicateColumn = (doc, columnId) => {
  const id = generateId(doc)
  doc.transact(() => {
    const columnMeta = doc.getMap('columnMeta')
    const existingMeta = columnMeta.get(columnId)
    if (!existingMeta) {
      throw new Error(`Column meta not found for columnId: ${columnId}`)
    }
    columnMeta.set(id, {...existingMeta})

    const data = getDataEntries(doc)
    for (const [_, row] of data) {
      const source = row.get(columnId)
      if (source !== undefined) {
        row.set(id, source)
      }
    }

    const columns = doc.getArray('columns')
    const index = columns.toArray().indexOf(columnId)
    columns.insert(index + 1, [id])
  })
  return id
}

export const deleteColumn = (doc, columnId) => {
  doc.transact(() => {
    const columns = doc.getArray('columns')
    const index = columns.toArray().indexOf(columnId)
    columns.delete(index, 1)

    const data = getDataEntries(doc)
    for (const [_, row] of data) {
      row.delete(columnId)
    }
  })
}

export const appendRow = (doc, userId, rowData) => {
  const id = generateId(doc)
  doc.transact(() => {
    const rows = doc.getArray('rows')
    const row = []
    if (rowData) {
      Object.entries(rowData).forEach(([columnId, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          row.push({key: columnId, val: value})
        }
      })
    }
    if (userId) {
      row.push({key: '_createdBy', val: userId})
      row.push({key: '_createdAt', val: Date.now()})
    }
    const data = getData(doc)
    data.set(id, Y.Array.from(row) )
    rows.push([id])
  })
  return id
}

export const deleteRow = (doc, rowId) => {
  doc.transact(() => {
    const rows = doc.getArray('rows')
    const index = rows.toArray().indexOf(rowId)
    if (index !== -1) {
      rows.delete(index, 1)
    }

    const data = getData(doc)
    data.delete(rowId)
  })
}
